/**
 * S3 Upload Utility for Frontend
 * Handles image uploads to AWS S3 bucket with progress tracking and error handling
 */

import { API_BASE_URL } from '../api';
import { CACHE_CONTROL_IMMUTABLE, getCdnUrl, getCdnDomain, normalizeImageUrl } from './cdn';

// S3 Configuration - these should be set via environment variables
const S3_CONFIG = {
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  bucketName: process.env.REACT_APP_S3_BUCKET_NAME || 'your-bucket-name',
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  // Optional: CloudFront distribution domain for CDN
  cloudFrontDomain: getCdnDomain()
};

// Log configuration status (without exposing sensitive data)
console.log('S3 Configuration Status:', {
  region: S3_CONFIG.region,
  bucketName: S3_CONFIG.bucketName,
  hasAccessKey: !!S3_CONFIG.accessKeyId,
  hasSecretKey: !!S3_CONFIG.secretAccessKey,
  hasCloudFront: !!S3_CONFIG.cloudFrontDomain
});

/**
 * Retry function with exponential backoff for handling rate limiting
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} delay - Initial delay in milliseconds
 * @returns {Promise<any>} - Result of the function
 */
async function retryWithBackoff(fn, maxRetries = 3, delay = 1000) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if ((error.status === 429 || error.message?.includes('rate limit')) && i < maxRetries - 1) {
        const waitTime = delay * Math.pow(2, i); // Exponential backoff
        console.log(`Rate limited, retrying in ${waitTime}ms... (attempt ${i + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }
      throw error;
    }
  }
}

/**
 * Upload image to S3 bucket
 * @param {File} file - The image file to upload
 * @param {Object} options - Upload options
 * @param {string} options.folder - Folder path in S3 (e.g., 'products', 'users')
 * @param {string} options.fileName - Custom filename (optional, will generate if not provided)
 * @param {Function} options.onProgress - Progress callback function (optional)
 * @param {boolean} options.publicRead - Whether the file should be publicly readable (default: true)
 * @param {string} options.contentType - Content type override (optional)
 * @param {number} options.maxRetries - Maximum retry attempts for rate limiting (default: 3)
 * @returns {Promise<Object>} - Upload result with URL and metadata
 */
export async function uploadImageToS3(file, options = {}) {
  const {
    folder = 'uploads',
    fileName = null,
    onProgress = null,
    onUploadProgress = null,
    contentType = null
  } = options;
  const progressCallback = onProgress || onUploadProgress;

  // Logged-in users: upload via backend (AWS credentials stay server-side).
  const hasAuthToken = typeof localStorage !== 'undefined' && !!localStorage.getItem('token');
  if (hasAuthToken) {
    return await uploadImageToS3ViaBackend(file, { ...options, onProgress: progressCallback });
  }

  // Anonymous uploads use presigned PUT URLs from the API.

  // Validate file
  if (!file || !(file instanceof File)) {
    throw new Error('Invalid file provided. Expected a File object.');
  }

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Unsupported file type: ${file.type}. Supported types: ${allowedTypes.join(', ')}`);
  }

  // Validate file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    throw new Error(`File size too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum allowed: 10MB`);
  }

  try {
    // Generate unique filename if not provided
    const finalFileName = fileName || generateUniqueFileName(file.name);
    
    // Create S3 key (path)
    const s3Key = `${folder}/${finalFileName}`;
    
    // Use retry logic for the entire upload process
    const uploadResult = await retryWithBackoff(async () => {
          // Get presigned URL for upload
    const presignedUrl = await getPresignedUrl(s3Key, file.type);
      
      // Upload file using presigned URL
      return await uploadWithPresignedUrl(presignedUrl, file, contentType || file.type, progressCallback);
    }, options.maxRetries || 3, 1000);
    
    // Generate final URL
    const finalUrl = getCdnUrl(s3Key);

    return {
      success: true,
      url: finalUrl,
      s3Key: s3Key,
      fileName: finalFileName,
      fileSize: file.size,
      contentType: file.type,
      uploadedAt: new Date().toISOString()
    };

  } catch (error) {
    console.error('S3 upload error:', error);
    throw new Error(`S3 upload failed: ${error.message}`);
  }
}

/**
 * Get presigned URL for S3 upload
 * @param {string} s3Key - S3 object key
 * @param {string} contentType - File content type
 * @param {boolean} publicRead - Whether file should be publicly readable
 * @returns {Promise<string>} - Presigned URL
 */
async function getPresignedUrl(s3Key, contentType) {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const presignPath = token ? '/s3/presigned-url' : '/s3/presigned-url/public';
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${presignPath}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        key: s3Key,
        contentType: contentType
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to get presigned URL: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.presignedUrl;

  } catch (error) {
    console.error('Error getting presigned URL:', error);
    throw new Error(`Failed to get presigned URL: ${error.message}`);
  }
}

/**
 * Upload file using presigned URL with progress tracking
 * @param {string} presignedUrl - Presigned URL for upload
 * @param {File} file - File to upload
 * @param {string} contentType - Content type
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} - Upload result
 */
async function uploadWithPresignedUrl(presignedUrl, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Progress tracking
    if (onProgress && typeof onProgress === 'function') {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });
    }

    // Handle response
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve({ success: true });
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    // Start upload
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('Cache-Control', CACHE_CONTROL_IMMUTABLE);
    xhr.send(file);
  });
}

/**
 * Generate unique filename with timestamp and random string
 * @param {string} originalName - Original filename
 * @returns {string} - Unique filename
 */
function generateUniqueFileName(originalName) {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const extension = originalName.split('.').pop();
  const nameWithoutExtension = originalName.substring(0, originalName.lastIndexOf('.'));
  
  // Clean filename (remove special characters)
  const cleanName = nameWithoutExtension.replace(/[^a-zA-Z0-9]/g, '_');
  
  return `${cleanName}_${timestamp}_${randomString}.${extension}`;
}

/**
 * Delete image from S3 bucket
 * @param {string} s3Key - S3 object key to delete
 * @returns {Promise<Object>} - Deletion result
 */
export async function deleteImageFromS3(s3Key) {
  try {
    const response = await fetch(`${API_BASE_URL}/s3/delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ key: s3Key })
    });

    if (!response.ok) {
      throw new Error(`Failed to delete file: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return { success: true, message: 'File deleted successfully' };

  } catch (error) {
    console.error('S3 delete error:', error);
    throw new Error(`Failed to delete file: ${error.message}`);
  }
}

/**
 * Get image URL from S3 key
 * @param {string} s3Key - S3 object key
 * @returns {string} - Full URL to the image
 */
export function getS3ImageUrl(s3Key) {
  return getCdnUrl(s3Key);
}

export { normalizeImageUrl };

/**
 * Validate S3 configuration
 * @returns {Object} - Validation result
 */
export function validateS3Config() {
  const issues = [];
  
  if (!S3_CONFIG.accessKeyId) {
    issues.push('AWS Access Key ID not configured');
  }
  
  if (!S3_CONFIG.secretAccessKey) {
    issues.push('AWS Secret Access Key not configured');
  }
  
  if (!S3_CONFIG.bucketName || S3_CONFIG.bucketName === 'your-bucket-name') {
    issues.push('S3 Bucket Name not configured');
  }
  
  if (!S3_CONFIG.region) {
    issues.push('AWS Region not configured');
  }
  
  return {
    isValid: issues.length === 0,
    issues: issues
  };
}

/**
 * Upload multiple images to S3
 * @param {File[]} files - Array of files to upload
 * @param {Object} options - Upload options
 * @returns {Promise<Object[]>} - Array of upload results
 */
export async function uploadMultipleImagesToS3(files, options = {}) {
  const results = [];
  const { onProgress = null } = options;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      // Create progress callback for individual files
      const individualProgress = onProgress ? (progress) => {
        const overallProgress = ((i + progress / 100) / files.length) * 100;
        onProgress(overallProgress);
      } : null;
      
      const result = await uploadImageToS3(file, {
        ...options,
        onProgress: individualProgress
      });
      
      results.push({ ...result, index: i });
      
    } catch (error) {
      results.push({ 
        success: false, 
        error: error.message, 
        fileName: file.name,
        index: i 
      });
    }
  }
  
  return results;
}

const CANVAS_OUTPUT_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);

/** True for iPhone HEIC/HEIF photos that browsers cannot draw to canvas directly. */
export function isHeicFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
}

/** Convert HEIC/HEIF to JPEG for browser upload pipelines. */
export async function convertHeicToJpeg(file) {
  const heic2any = (await import('heic2any')).default;
  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92,
  });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  if (!blob) {
    throw new Error('HEIC conversion returned empty result');
  }
  const baseName = String(file.name || 'image').replace(/\.[^/.]+$/, '') || 'image';
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/** Normalize exotic phone camera formats to a browser-friendly image file. */
export async function normalizeImageFile(file) {
  if (isHeicFile(file)) {
    return convertHeicToJpeg(file);
  }
  return file;
}

/**
 * Compress image before upload (client-side compression)
 * @param {File} file - Original image file
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width (default: 1920)
 * @param {number} options.maxHeight - Maximum height (default: 1080)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @returns {Promise<File>} - Compressed file
 */
export async function compressImage(file, options = {}) {
  const { maxWidth = 1920, maxHeight = 1080, quality = 0.8 } = options;

  let sourceFile = file;
  try {
    sourceFile = await normalizeImageFile(file);
  } catch (error) {
    const hint = isHeicFile(file)
      ? 'Could not process HEIC photo. Try saving as JPG in Photos and upload again.'
      : 'Could not prepare image for upload.';
    throw new Error(`${hint} ${error.message || ''}`.trim());
  }

  const blobType = CANVAS_OUTPUT_TYPES.has(sourceFile.type) ? sourceFile.type : 'image/jpeg';
  const outputName =
    blobType === 'image/jpeg' && !/\.jpe?g$/i.test(sourceFile.name)
      ? `${String(sourceFile.name).replace(/\.[^/.]+$/, '') || 'image'}.jpg`
      : sourceFile.name;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const objectUrl = URL.createObjectURL(sourceFile);

    img.onload = () => {
      let { width, height } = img;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }
          resolve(
            new File([blob], outputName, {
              type: blobType,
              lastModified: Date.now(),
            })
          );
        },
        blobType === 'image/png' ? 'image/png' : 'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(
        new Error(
          isHeicFile(file)
            ? 'Failed to load HEIC image for compression'
            : 'Failed to load image for compression. Use JPG, PNG, WEBP, or HEIC.'
        )
      );
    };

    img.src = objectUrl;
  });
}

/**
 * Create a small JPEG thumbnail file for card/grid display.
 * @param {File} file - Original image file
 * @param {Object} options - Thumbnail options
 * @param {number} options.size - Maximum width/height (default: 480)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.72)
 * @returns {Promise<File>} - Thumbnail JPEG file
 */
export async function createThumbnailImageFile(file, options = {}) {
  const { size = 480, quality = 0.72 } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const scale = Math.min(size / img.width, size / img.height, 1);
        const width = Math.max(1, Math.round(img.width * scale));
        const height = Math.max(1, Math.round(img.height * scale));

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const baseName = file.name.replace(/\.[^/.]+$/, '') || 'thumbnail';
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) {
            reject(new Error('Failed to create thumbnail image'));
            return;
          }
          resolve(new File([blob], `${baseName}-thumbnail.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now()
          }));
        }, 'image/jpeg', quality);
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for thumbnail creation'));
    };

    img.src = objectUrl;
  });
}

/**
 * Upload image to S3 via backend proxy (fallback for CORS issues)
 * @param {File} file - File to upload
 * @param {Object} options - Upload options
 * @returns {Promise<Object>} - Upload result
 */
export async function uploadImageToS3ViaBackend(file, options = {}) {
  try {
    console.log('Backend proxy upload - API Base URL:', API_BASE_URL);
    console.log('Backend proxy upload - File:', file.name, file.size, file.type);
    console.log('Backend proxy upload - Options:', options);
    
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', options.folder || 'uploads');
    formData.append('fileName', options.fileName || generateUniqueFileName(file.name));
    
    const token = localStorage.getItem('token');
    console.log('Backend proxy upload - Token available:', !!token);
    
    const response = await fetch(`${API_BASE_URL}/s3/upload`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('Backend proxy upload - Response status:', response.status);
    console.log('Backend proxy upload - Response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Backend proxy upload - Error response:', errorText);
      throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log('Backend proxy upload - Success result:', result);
    return result;
  } catch (error) {
    console.error('Backend upload error:', error);
    throw error;
  }
}

/**
 * Upload image to S3 with fallback to backend proxy
 * @param {File} file - File to upload
 * @param {Object} options - Upload options
 * @returns {Promise<Object>} - Upload result
 */
export async function uploadImageWithFallback(file, options = {}) {
  const hasAuthToken = typeof localStorage !== 'undefined' && !!localStorage.getItem('token');
  if (hasAuthToken) {
    return await uploadImageToS3ViaBackend(file, options);
  }

  try {
    return await uploadImageToS3(file, options);
  } catch (error) {
    console.warn('Presigned upload failed, trying backend proxy:', error);
    try {
      return await uploadImageToS3ViaBackend(file, options);
    } catch (backendError) {
      console.error('Both upload methods failed:', backendError);
      throw new Error(`Upload failed: ${error.message}. Fallback also failed: ${backendError.message}`);
    }
  }
} 